import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { HttpError } from 'src/app/core/http-error';
import { NotificationService } from 'src/app/services/notification-service';
import { ICopyable } from '../../common/icopyable';
import { IPaginable } from '../../common/ipaginable';
import { ISearchable } from '../../common/isearchable';
import { PaginationInfos } from '../../infinite-scroll-pagination/models/pagination-infos';
import { Context } from '../models/context';
import { FilterInfos } from '../models/filter-infos';
import { Results } from '../models/results-T';
import { SortInfos } from '../models/sort-infos';
import { AdvancedService } from '../services/advanced-service';
export class AdvancedDataSource<T extends ICopyable<T>> implements DataSource<T>, ISearchable, IPaginable {
    public datas: T[] = [];
    public datasSubject = new BehaviorSubject<T[]>([]);
    public loadingSubject = new BehaviorSubject<boolean>(false);
    public paginationSubject = new BehaviorSubject<PaginationInfos>(new PaginationInfos());

    public loading$ = this.loadingSubject.asObservable();

    public datas$ = this.datasSubject.asObservable();

    private currentFilters: FilterInfos[];
    private currentGeneralFilters: FilterInfos[];
    private currentSortColumn: string;
    private currentSortDirection: string;
    private currentPageSize: number;

    constructor(
        private context: Context,
        private service: AdvancedService<T>,
        private spinner: NgxSpinnerService,
        private notificationService: NotificationService
    ) {}

    connect(collectionViewer: CollectionViewer): Observable<T[]> {
        return this.datasSubject.asObservable();
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this.datasSubject.complete();
        this.loadingSubject.complete();
        this.paginationSubject.complete();
    }

    loadRecords(filters: FilterInfos[], generalfilters: FilterInfos[], sortColumn = '', sortDirection = 'asc', pageIndex = 0, pageSize = 100, append: boolean) {
        this.currentFilters = filters;
        this.currentGeneralFilters = generalfilters;
        this.currentSortColumn = sortColumn;
        this.currentSortDirection = sortDirection;
        this.currentPageSize = pageIndex + pageSize;

        this.loadingSubject.next(true);
        this.spinner.show('main-spinner');

        var sortInfos = new SortInfos(sortColumn, sortDirection);

        this.service
            .List(this.context, filters, generalfilters, sortInfos, pageIndex, pageSize)
            .pipe(
                catchError((err: HttpError) => {
                    this.notificationService.displayError('Erreur de chargement', `Erreur lors du chargement des données. ${err.message}`);
                    let empty = new Results<T>();
                    empty.value = [];
                    empty.resultCount = 0;
                    empty.totalCount = 0;

                    return of(empty);
                }),
                finalize(() => {
                    this.loadingSubject.next(false);
                    this.spinner.hide('main-spinner');
                })
            )
            .subscribe((x) => {
                if (append) {
                    for (let i = 0; i < x.value.length; ++i) {
                        this.datas.push(x.value[i]);
                    }
                } else {
                    this.datas = x.value;
                }

                this.datasSubject.next(this.datas);

                var paginationInfos = new PaginationInfos();
                paginationInfos.filteredRecordsCount = x.totalCount;
                paginationInfos.LoadedRecordsRangeStart = 0;
                paginationInfos.LoadedRecordsRangeEnd = this.datas.length;

                this.paginationSubject.next(paginationInfos);
            });
    }

    public reloadRecords(): void {
        this.loadRecords(this.currentFilters, this.currentGeneralFilters, this.currentSortColumn, this.currentSortDirection, 0, this.currentPageSize, false);
    }

    public refresh() {
        this.datasSubject.next(this.datas);
    }

    /**
     * Exporte les données de la datasource.
     * @param exportFileName : Nom du fichier d'export.
     */
    public export(exportFileName: string) {
        var sortInfos = new SortInfos(this.currentSortColumn, this.currentSortDirection);

        this.spinner.show('main-spinner');

        this.service
            .Export(this.context, this.currentFilters, this.currentFilters, sortInfos)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe({
                next: (blob) => {
                    const a = document.createElement('a');
                    const objectUrl = URL.createObjectURL(blob);
                    a.href = objectUrl;
                    a.download = exportFileName;
                    a.click();
                    URL.revokeObjectURL(objectUrl);
                    this.spinner.hide('main-spinner');
                },
                error: () => {
                    this.spinner.hide('main-spinner');
                },
            });
    }

    public getRecordsCount(): number {
        return this.datas.length;
    }
}
